import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ConfigProvider } from "antd";
import "@aws-amplify/ui-react/styles.css";
import "./index.css";
import Dashboard from "./components/Dashboard";
import JwtInterceptor from "auth/jwtInterceptor";
import { SimulatorProvider } from "./store/context";
import Alerts from "./components/Alerts";
import LocationBasedServices from "./components/locationBasedServices/LocationBasedServices";
import LiveLogsSimulator from "./components/LiveLogsSimulator";
import RemoteFunctions from "./components/RemoteFunctions";
import AndroidTestClient from "./components/AndroidTestClient";
import Safety from "./components/Safety";
import Navigation from "./common/Navigation";
import VehicleOverview from "components/VehicleOverview";
import FaultDetection from "components/FaultDetection";
import DTCSignals from "components/DTCSignals";
import AuthStyle from "AuthStyle";
import ValetMode from "./components/ValetMode";

JwtInterceptor();

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_AWS_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_AWS_COGNITO_OTA_APP_CLIENT_ID,
    },
  },
});

const simulatorTheme = {
  token: {
    colorPrimary: "#2563eb",
    colorBgLayout: "#f1f5f9",
    colorBgContainer: "#ffffff",
    colorBorder: "#e2e8f0",
    colorTextBase: "#0f172a",
    colorTextSecondary: "#64748b",
    borderRadius: 8,
    borderRadiusLG: 12,
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 14,
    boxShadow:
      "0 1px 3px 0 rgb(0 0 0 / 0.08), 0 1px 2px -1px rgb(0 0 0 / 0.06)",
  },
  components: {
    Layout: {
      headerBg: "#ffffff",
      siderBg: "#ffffff",
      bodyBg: "#f1f5f9",
      footerBg: "#f8fafc",
    },
    Menu: {
      itemBorderRadius: 6,
      itemHeight: 42,
      groupTitleColor: "#94a3b8",
      groupTitleFontSize: 11,
    },
    Card: {
      headerBg: "transparent",
      boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.06)",
    },
  },
};

const NavWrapper = ({ user, children }) => {
  return <Navigation user={user}>{children}</Navigation>;
};

const AppRoutes = ({ user }) => {
  return (
    <BrowserRouter>
      <SimulatorProvider>
        <Routes>
          <Route
            path="/*"
            element={
              <NavWrapper user={user}>
                <Dashboard />
              </NavWrapper>
            }
          />
          <Route
            path="/alerts"
            element={
              <NavWrapper user={user}>
                <Alerts />
              </NavWrapper>
            }
          />
          <Route
            path="/location-based-services"
            element={
              <NavWrapper user={user}>
                <LocationBasedServices />
              </NavWrapper>
            }
          />
          <Route path="/live-logs-simulator">
            <Route
              index
              element={
                <NavWrapper user={user}>
                  <LiveLogsSimulator />
                </NavWrapper>
              }
            />
            <Route
              path="fault-detection"
              element={
                <NavWrapper user={user}>
                  <FaultDetection />
                </NavWrapper>
              }
            />
          </Route>
          <Route
            path="/remote-functions"
            element={
              <NavWrapper user={user}>
                <RemoteFunctions
                  onLiveTrackingShadowUpdate={() => {}}
                  portalControls={false}
                />
              </NavWrapper>
            }
          />
          <Route
            path="/valet-mode"
            element={
              <NavWrapper user={user}>
                <ValetMode />
              </NavWrapper>
            }
          />
          <Route
            path="/vehicle-overview"
            element={
              <NavWrapper user={user}>
                <VehicleOverview />
              </NavWrapper>
            }
          />
          <Route
            path="/dtc-signals"
            element={
              <NavWrapper user={user}>
                <DTCSignals />
              </NavWrapper>
            }
          />
          <Route
            path="/android-test-client"
            element={
              <NavWrapper user={user}>
                <AndroidTestClient />
              </NavWrapper>
            }
          />
          <Route
            path="/safety"
            element={
              <NavWrapper user={user}>
                <Safety />
              </NavWrapper>
            }
          />
        </Routes>
      </SimulatorProvider>
    </BrowserRouter>
  );
};

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(document.getElementById("root"));

const fadeOutPreloader = () => {
  const preloader = document.getElementById('preloader');
  if (preloader) {
    preloader.classList.add('pl-hidden');
    setTimeout(() => preloader.remove(), 600);
  }
};

root.render(
  <React.StrictMode>
    <ConfigProvider theme={simulatorTheme}>
      <QueryClientProvider client={queryClient}>
        <AuthStyle AppRoutes={AppRoutes} />
      </QueryClientProvider>
    </ConfigProvider>
  </React.StrictMode>
);

setTimeout(fadeOutPreloader, 300);
